import { manualTaskService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';

import { CASES_QUERY_CONFIG } from '@/constants/queryConfig';
import { paginateCursorList } from '@/utils/paginateCursorList';

interface UseAllOrgTasksParams {
  created_after?: string;
  created_before?: string;
  user_ids?: string[];
  task_id?: string;
  case_record_id?: string;
}

// One request of up to 500 rows (the server's cap for this RPC), not 5 × 100: each
// request re-resolves the caller's readable programs and counts against the rate limit.
const MAX_PAGES = 1;
const PAGE_SIZE = 500;

/**
 * Fetches every assignee's manual tasks in the selected organization (All
 * Tasks page) for the given filters, up to MAX_PAGES × PAGE_SIZE rows. The
 * server limits them to programs where the caller can read every case. Tabs and
 * counts are computed client-side over this one dataset, as on My Tasks;
 * `reachedCap` says there were more rows than were fetched.
 */
export const useAllOrgTasks = (params: UseAllOrgTasksParams, enabled = true) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const query = useQuery({
    queryKey: queryKeys.orgTasks.byOrg(selectedOrganizationId || '', {
      ...params,
      pageSize: PAGE_SIZE,
      maxPages: MAX_PAGES,
    }),
    queryFn: () =>
      paginateCursorList(
        async cursor => {
          const res = await manualTaskService.listOrganizationManualTasks({
            organization_id: selectedOrganizationId!,
            ...params,
            limit: PAGE_SIZE,
            start_after_id: cursor,
          });
          return { records: res.manual_tasks, next_cursor: res.next_cursor };
        },
        MAX_PAGES,
        PAGE_SIZE,
      ),
    enabled: enabled && !!selectedOrganizationId,
    // Same reasoning as useAllMyTasks: no 30s polling for a multi-page fan-out.
    ...CASES_QUERY_CONFIG,
    refetchInterval: false,
  });

  return {
    ...query,
    tasks: query.data?.records ?? [],
    reachedCap: query.data?.reachedCap ?? false,
    maxRows: MAX_PAGES * PAGE_SIZE,
  };
};
